export enum MultiBuyerRole {
  MultiBuyer = 'multiBuyer',
  MasterMultiBuyer = 'masterMultiBuyer',
  MbManager = 'mbManager',
  None = 'none',
}

export enum EntityType {
  Owned = 'owned',
  ServicePartner = 'servicePartner',
}

export enum MultiBuyerCompany {
  YouGov = 'yougov',
  Quantilope = 'quantilope',
  None = 'none',
}

export const MULTI_BUYER_ROLES_NUM_MASK: Record<MultiBuyerRole, number> = {
  [MultiBuyerRole.MultiBuyer]: 6,
  [MultiBuyerRole.MbManager]: 6,
  [MultiBuyerRole.MasterMultiBuyer]: 7,
  [MultiBuyerRole.None]: 0,
};

export function isMultiBuyerRole(
  value: MultiBuyerRole | undefined | null
): boolean {
  return value != null && value !== MultiBuyerRole.None;
}

export interface AccessibleEntity {
  id: number;
  type: EntityType;
}

export interface BuyerCapabilities {
  canManageBuyerSettings: boolean;
  canCreateTemplate: boolean;
  canSeeMarginAndBank: boolean;
  hasAccessToTemplate: boolean;
  canSeeCustomTemplates: boolean;
  canManageSupplierSettings: boolean;
  canSeeSupplierBlends: boolean;
  canSeeOperatorProjectManager: boolean;
  canSeeCrmId: boolean;
  canAccessMasterTemplate: boolean;
}

export interface AccessControlConfig {
  clientId: number;
  userRole: MultiBuyerRole;
  clientName: MultiBuyerCompany;
  buyerAcssLvls: number;
  multiBuyerCmps: Array<number>;
  capabilities: BuyerCapabilities;
  accessibleEntities: AccessibleEntity[];
}

const FULL_ACCESS_CAPABILITIES: BuyerCapabilities = {
  canSeeMarginAndBank: true,
  canCreateTemplate: true,
  canManageBuyerSettings: true,
  hasAccessToTemplate: true,
  canSeeCustomTemplates: false,
  canManageSupplierSettings: true,
  canSeeSupplierBlends: true,
  canAccessMasterTemplate: false,
  canSeeOperatorProjectManager: false,
  canSeeCrmId: false,
};

const LOCKED_CAPABILITIES: BuyerCapabilities = {
  canSeeMarginAndBank: false,
  canCreateTemplate: false,
  canManageBuyerSettings: false,
  hasAccessToTemplate: false,
  canSeeCustomTemplates: false,
  canManageSupplierSettings: false,
  canSeeSupplierBlends: false,
  canAccessMasterTemplate: false,
  canSeeOperatorProjectManager: false,
  canSeeCrmId: false,
};

export class AccessConfig implements AccessControlConfig {
  private constructor(
    readonly clientId: number,
    readonly userRole: MultiBuyerRole,
    readonly clientName: MultiBuyerCompany,
    readonly buyerAcssLvls: number,
    readonly multiBuyerCmps: number[],
    readonly capabilities: BuyerCapabilities,
    readonly accessibleEntities: AccessibleEntity[],
    readonly isReady: boolean
  ) {}

  static fromApiResponse(data: AccessControlConfig): AccessConfig {
    return new AccessConfig(
      data.clientId,
      data.userRole,
      data.clientName,
      data.buyerAcssLvls,
      data.multiBuyerCmps,
      data.capabilities,
      data.accessibleEntities,
      true
    );
  }

  static default(): AccessConfig {
    return new AccessConfig(
      0,
      MultiBuyerRole.None,
      MultiBuyerCompany.None,
      0,
      [0],
      FULL_ACCESS_CAPABILITIES,
      [],
      true
    );
  }

  static uninitialized(): AccessConfig {
    return new AccessConfig(
      0,
      MultiBuyerRole.None,
      MultiBuyerCompany.None,
      0,
      [],
      LOCKED_CAPABILITIES,
      [],
      false
    );
  }
}

export interface IMultiBuyerPermissions {
  canManageBuyerSettings(): boolean;
  canCreateTemplate(): boolean;
  canSeeMarginAndBank(): boolean;
  hasAccessToTemplate(): boolean;
  canSeeCustomTemplates(): boolean;
  canSeeOperatorProjectManager(): boolean;
  canSeeCrmId(): boolean;
  canManageSupplierSettings(): boolean;
  canSeeSupplierBlends(): boolean;
  canAccessMasterTemplate(): boolean;
}
